import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { AlertCircle, TrendingUp, Target } from 'lucide-react';
import { useFarmingResultsSelection } from '../../context/FarmingResultsSelectionContext';
import {
  fetchStillActiveWords,
  StillActiveWordsSummary
} from '../../services/stillActiveWordsService';

export const StillActiveWordsDashboard: React.FC = () => {
  const { selectedCompany, selectedProduct } = useFarmingResultsSelection();
  const [data, setData] = useState<StillActiveWordsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!selectedCompany || !selectedProduct) return;

    const asin = selectedProduct.id || selectedProduct.ASIN;
    if (!asin) {
      setError('No ASIN found for selected product');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`Fetching Still Active Words data for: ${selectedCompany}/${asin}`);
      const result = await fetchStillActiveWords(selectedCompany, asin);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch Still Active Words data';
      console.error('Error fetching Still Active Words:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, selectedProduct]);

  useEffect(() => {
    if (selectedCompany && selectedProduct) {
      fetchData();
    }
  }, [selectedCompany, selectedProduct, fetchData]);

  if (!selectedCompany || !selectedProduct) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <Target className="h-12 w-12 text-app-primary mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Select Company & ASIN
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Choose a company and ASIN from the sidebar to view Still Active Words data.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-app-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading Still Active Words...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Error Loading Data
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error}
          </p>
          <Button onClick={fetchData} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-gray-500 dark:text-gray-400 py-8">
        No data available
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Total Active Words
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-app-primary">
              {data.totalActiveWords.toLocaleString()}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Still active with 0 sales</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Word Click Threshold
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {data.wordClickThreshold}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Max clicks for words</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-app-primary" />
            Still Active Words
          </CardTitle>
          <CardDescription>
            Words that are still active with zero sales, sorted by clicks descending (limited to 190 entries)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.activeWords.length === 0 ? (
            <div className="text-center py-8">
              <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No still active words found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-full">
                <div className="flex border-b dark:border-gray-700 py-3 px-2 font-semibold text-gray-900 dark:text-gray-100">
                  <div className="w-1/4 text-left pr-4">Still Active Words</div>
                  <div className="w-1/4 text-right pr-4">Clicks</div>
                  <div className="w-1/4 text-right pr-4">Probability</div>
                  <div className="w-1/4 text-right">Killed in X Clicks</div>
                </div>
                
                <div className="space-y-0">
                  {data.activeWords.map((word, index) => (
                    <div key={`${word.term}-${index}`} className="flex border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 py-3 px-2">
                      <div className="w-1/4 pr-4">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {word.term}
                        </div>
                      </div>
                      <div className="w-1/4 text-right text-gray-900 dark:text-gray-100 pr-4">
                        {word.clicks.toLocaleString()}
                      </div>
                      <div className="w-1/4 text-right text-gray-900 dark:text-gray-100 pr-4">
                        {word.probability !== undefined ? `${(word.probability * 100).toFixed(0)}%` : '-'}
                      </div>
                      <div className="w-1/4 text-right">
                        {word.killedInClicks !== undefined ? (
                          <span className={`font-medium ${
                            word.killedInClicks < 0
                              ? 'text-red-600 dark:text-red-400'
                              : word.killedInClicks < 5
                              ? 'text-orange-600 dark:text-orange-400'
                              : 'text-green-600 dark:text-green-400'
                          }`}>
                            {word.killedInClicks}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StillActiveWordsDashboard;